import { Avatar } from 'ui';
import Drawer from 'core/modules/components/Drawer/Drawer';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { INTENT_COLOR_INDEX, INTENT_LABEL_KEYS } from '../../../../../constants/intentTypes';
import type { ScaleType } from '../QuadrantChart/scaleUtils';
import { normalizeValue } from '../QuadrantChart/scaleUtils';
import type { StudentQuadrantData } from '../types';

import './_student-xray-drawer.scss';

type StudentXrayDrawerProps = {
  maxQualityScore: number;
  onClose: () => void;
  open: boolean;
  qualityScaleType: ScaleType;
  student: StudentQuadrantData | null;
};

type IntentRow = {
  colorIndex: number;
  intentType: string;
  label: string;
  percentage: number;
  score: number;
};

function StudentXrayDrawer({ maxQualityScore, onClose, open, qualityScaleType, student }: StudentXrayDrawerProps): JSX.Element | null {
  const { t } = useTranslation(['chatbot']);

  const qualityPercentage = useMemo(() => {
    if (!student || maxQualityScore === 0) return 0;
    const normalized = normalizeValue(student.qualityScore, maxQualityScore, qualityScaleType);
    return Math.round(normalized * 100);
  }, [maxQualityScore, qualityScaleType, student]);

  const intentRows: IntentRow[] = useMemo(() => {
    if (!student?.intentCounts) return [];

    const totalScore = Object.values(student.intentCounts).reduce((sum, intent) => {
      return sum + (intent.totalScore ?? 0);
    }, 0);

    if (totalScore === 0) return [];

    return Object.entries(student.intentCounts)
      .filter(([, intent]) => intent.totalScore !== null && intent.totalScore > 0)
      .map(([intentType, intent]) => {
        const score = intent.totalScore ?? 0;
        return {
          colorIndex: INTENT_COLOR_INDEX[intentType] ?? 1,
          intentType,
          label: INTENT_LABEL_KEYS[intentType] ? t(`chatbot:${INTENT_LABEL_KEYS[intentType]}`) : intentType,
          percentage: Math.round((score / totalScore) * 100),
          score,
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [student, t]);

  const maxIntentScore = intentRows.length > 0 ? intentRows[0].score : 0;

  if (!student) return null;

  const header = (
    <div className="student-xray-drawer__header">
      <div className="student-xray-drawer__student-info">
        <Avatar image={student.avatar || undefined} size="md" />
        <div className="student-xray-drawer__student-text">
          <span className="student-xray-drawer__student-name">
            {student.name} {student.lastName}
          </span>
          {student.groupName && <span className="student-xray-drawer__student-class">{student.groupName}</span>}
        </div>
      </div>
      <button aria-label="Close" className="student-xray-drawer__close-btn" onClick={onClose} type="button">
        <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        </svg>
      </button>
    </div>
  );

  return (
    <Drawer header={header} onClose={onClose} open={open} position="right" width={360}>
      <div className="student-xray-drawer__body">
        {/* Stats row */}
        <div className="student-xray-drawer__stats">
          <div className="student-xray-drawer__stat-card">
            <span className="student-xray-drawer__stat-label">{t('chatbot:ai_analytics_xray_drawer_volume')}</span>
            <div className="student-xray-drawer__stat-value-row">
              <span className="student-xray-drawer__stat-value">{student.totalMessages}</span>
              <span className="student-xray-drawer__stat-unit">{t('chatbot:ai_analytics_xray_drawer_messages')}</span>
            </div>
          </div>
        </div>

        {/* Quality bar */}
        <div className="student-xray-drawer__quality-section">
          <div className="student-xray-drawer__quality-header">
            <span className="student-xray-drawer__quality-label">{t('chatbot:ai_analytics_xray_drawer_quality')}</span>
            <span className="student-xray-drawer__quality-percentage">{qualityPercentage}%</span>
          </div>
          <div className="student-xray-drawer__quality-bar">
            <div className="student-xray-drawer__quality-bar-fill" style={{ width: `${Math.min(100, qualityPercentage)}%` }} />
          </div>
          <p className="student-xray-drawer__quality-description">{t('chatbot:ai_analytics_xray_drawer_quality_description')}</p>
        </div>

        {/* Intent distribution */}
        <div className="student-xray-drawer__intent-section">
          <h3 className="student-xray-drawer__intent-section-title">{t('chatbot:ai_analytics_xray_drawer_intent_distribution')}</h3>
          <div className="student-xray-drawer__intent-list">
            {intentRows.map((row) => {
              const barWidth = maxIntentScore > 0 ? Math.round((row.score / maxIntentScore) * 100) : 0;
              return (
                <div className={`student-xray-drawer__intent-row student-xray-drawer__intent-row--color-${row.colorIndex}`} key={row.intentType}>
                  <div className="student-xray-drawer__intent-header">
                    <span className="student-xray-drawer__intent-label">{row.label}</span>
                    <span className="student-xray-drawer__intent-percentage">{row.percentage}%</span>
                  </div>
                  <div className="student-xray-drawer__intent-bar">
                    <div className="student-xray-drawer__intent-bar-fill" style={{ width: `${barWidth}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Drawer>
  );
}

export default StudentXrayDrawer;
